import { JobCard } from '@agentic-flow-studio/shared/components/job-plan/JobCard';
import { JobPlanDetailPanel } from '@agentic-flow-studio/shared/components/job-plan/JobPlanDetailPanel';
import { JobPlanMap } from '@agentic-flow-studio/shared/components/job-plan/JobPlanMap';
import { JobPlanTimeline } from '@agentic-flow-studio/shared/components/job-plan/JobPlanTimeline';
import { JobPreviewSheet } from '@agentic-flow-studio/shared/components/job-plan/JobPreviewSheet';
import { JobSlotFinderDialog } from '@agentic-flow-studio/shared/components/job-plan/JobSlotFinderDialog';
import { checkOperatorSkillEligibility } from '@agentic-flow-studio/shared/lib/job-plan-eligibility';
import {
  type RecomputedStopTiming,
  recomputeStopSequence,
} from '@agentic-flow-studio/shared/lib/job-plan-sequence';
import { formatOffsetTime } from '@agentic-flow-studio/shared/lib/job-plan-time';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type {
  Job,
  OperatorProfile,
  RouteAssignment,
  RouteStop,
} from '@agentic-flow-studio/shared/types/job-plan';
import type {
  OptimizationStrategy,
  SolverResponse,
} from '@agentic-flow-studio/shared/types/job-plan-solver';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@agentic-flow-studio/shared/ui/dialog';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agentic-flow-studio/shared/ui/select';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  ArrowUp,
  Calendar as CalendarIcon,
  Check,
  CheckCircle,
  CheckCircle2,
  Clock,
  Download,
  Fuel,
  LayoutGrid,
  Map as MapIcon,
  Navigation,
  Plus,
  RefreshCw,
  Scale,
  Search,
  Sparkles,
  Trash2,
  TrendingUp,
  UserX,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { JobPlanCopilotPanel } from '../components/chat/JobPlanCopilotPanel';
import { JobPlanOnboardingModal } from '../components/JobPlanOnboardingModal';

interface StrategyOption {
  id: OptimizationStrategy;
  title: string;
  badge?: string;
  icon: React.ComponentType<{ className?: string }>;
  goal: string;
  behavior: string;
  bestFor: string;
  colorClass: string;
}

const STRATEGY_OPTIONS: StrategyOption[] = [
  {
    id: 'balanced',
    title: 'Balanced Workload',
    badge: 'Recommended',
    icon: Scale,
    goal: 'Equal distribution of stops and driving time across all active operators.',
    behavior: 'Distributes jobs evenly while respecting time windows and operator shift end times.',
    bestFor: 'General daily operations, employee morale, fair work division.',
    colorClass: 'border-primary/60 bg-primary/5',
  },
  {
    id: 'fastest_finish',
    title: 'Fastest Day Finish',
    icon: Zap,
    goal: 'Minimizes the latest departure time so all operators finish their shifts as early as possible.',
    behavior:
      'Optimizes route concurrency to bring the maximum route completion time to a minimum.',
    bestFor: 'High-urgency days, severe weather inbound, early wrap-up targets.',
    colorClass: 'border-amber-500/60 bg-amber-500/5',
  },
  {
    id: 'min_drive',
    title: 'Minimize Total Distance',
    icon: Fuel,
    goal: 'Minimizes cumulative vehicle travel distance and fuel consumption.',
    behavior:
      'Packs stops into tight geographic clusters on the fewest operators necessary, keeping reserve operators free.',
    bestFor: 'Subcontractors with mileage pay, long-distance routes, vehicle wear reduction.',
    colorClass: 'border-emerald-500/60 bg-emerald-500/5',
  },
];

type ViewMode = 'board' | 'timeline' | 'map';

export default function SchedulePage() {
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const today = new Date().toISOString().split('T')[0];
  const [serviceDate, setServiceDateState] = useState<string>(dateParam || today);

  const setServiceDate = (newDate: string) => {
    setServiceDateState(newDate);
    setSearchParams({ date: newDate }, { replace: true });
  };

  const openIngest = () => {
    navigate(`/org/${orgId}/workspace/${workspaceId}/schedule/ingest?serviceDate=${serviceDate}`);
  };

  useEffect(() => {
    if (dateParam && dateParam !== serviceDate) {
      setServiceDateState(dateParam);
    }
  }, [dateParam, serviceDate]);

  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [isOnboardingOpen, setIsOnboardingOpen] = useState<boolean>(false);
  const [isCopilotOpen, setIsCopilotOpen] = useState<boolean>(true);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState<boolean>(true);
  const [assigningJob, setAssigningJob] = useState<Job | null>(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
  const [poolSearchQuery, setPoolSearchQuery] = useState<string>('');
  const [previewJob, setPreviewJob] = useState<Job | null>(null);
  const [previewJobId, setPreviewJobId] = useState<number | null>(null);
  const [isSlotFinderOpen, setIsSlotFinderOpen] = useState<boolean>(false);

  const { data: jobPlanSettings } = trpc.jobPlan.getSettings.useQuery();

  useEffect(() => {
    if (jobPlanSettings && !jobPlanSettings.onboardingCompletedAt) {
      setIsOnboardingOpen(true);
    }
  }, [jobPlanSettings]);

  // Reassign Modal State
  const [reassigningStop, setReassigningStop] = useState<{
    stopId: number;
    jobTitle: string;
    currentOpId: number;
  } | null>(null);
  const [targetReassignOpId, setTargetReassignOpId] = useState<string>('');

  const [isStrategyModalOpen, setIsStrategyModalOpen] = useState<boolean>(false);
  const [selectedStrategy, setSelectedStrategy] = useState<OptimizationStrategy>('balanced');
  const [planViewMode, setPlanViewMode] = useState<'proposal' | 'live'>('proposal');

  const [proposedOptimization, setProposedOptimization] = useState<{
    runId: number;
    solverResult: SolverResponse;
    strategy: OptimizationStrategy;
  } | null>(null);

  const utils = trpc.useUtils();

  const { data } = trpc.jobPlan.getDaySchedule.useQuery(
    { serviceDate },
    { refetchInterval: 30000 }
  );

  const assignJobMutation = trpc.jobPlan.assignJob.useMutation({
    onSuccess: () => {
      toast.success('Job assigned successfully');
      setAssigningJob(null);
      setSelectedOperatorId('');
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
    },
    onError: (err) => toast.error(err.message),
  });

  const removeStopMutation = trpc.jobPlan.removeStop.useMutation({
    onSuccess: () => {
      toast.success('Stop unassigned and returned to pool');
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
    },
    onError: (err) => toast.error(err.message),
  });

  const moveStopPositionMutation = trpc.jobPlan.moveStopPosition.useMutation({
    onSuccess: () => {
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
    },
    onError: (err) => toast.error(err.message),
  });

  const reassignStopMutation = trpc.jobPlan.reassignStop.useMutation({
    onSuccess: () => {
      toast.success('Stop reassigned to operator');
      setReassigningStop(null);
      setTargetReassignOpId('');
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
    },
    onError: (err) => toast.error(err.message),
  });

  const clearRouteMutation = trpc.jobPlan.clearRoute.useMutation({
    onSuccess: (res) => {
      toast.success(`Cleared ${res.clearedCount} stop(s) back to unassigned pool`);
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
    },
    onError: (err) => toast.error(err.message),
  });

  const optimizeDayMutation = trpc.jobPlan.optimizeDay.useMutation({
    onSuccess: (result) => {
      const placedCount = result.solverResult.routes.reduce((sum, r) => sum + r.stops.length, 0);
      if (result.solverResult.status === 'no_solution' || placedCount === 0) {
        toast.error('Optimizer could not place any jobs -- see the proposal banner below for why.');
      } else if (result.solverResult.unplacedJobs.length > 0) {
        toast.warning(
          `Placed ${placedCount} job(s), but ${result.solverResult.unplacedJobs.length} couldn't be scheduled -- review before applying.`
        );
      } else {
        toast.success(`Optimization proposal generated -- all ${placedCount} jobs placed.`);
      }
      setProposedOptimization({
        runId: result.run.id,
        solverResult: result.solverResult,
        strategy: selectedStrategy,
      });
      setPlanViewMode('proposal');
    },
    onError: (err) => {
      toast.error(`Optimization failed: ${err.message}`);
    },
  });

  const acceptRunMutation = trpc.jobPlan.acceptRun.useMutation({
    onSuccess: () => {
      toast.success('Optimization plan accepted & applied');
      setProposedOptimization(null);
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
    },
    onError: (err) => toast.error(err.message),
  });

  const discardRunMutation = trpc.jobPlan.discardRun.useMutation({
    onSuccess: () => {
      toast.info('Optimization proposal discarded');
      setProposedOptimization(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const shiftDate = (deltaDays: number) => {
    const d = new Date(serviceDate);
    d.setDate(d.getDate() + deltaDays);
    setServiceDate(d.toISOString().split('T')[0]);
  };

  const liveAssignments = data?.assignments ?? [];
  const liveUnassignedJobs = data?.unassignedJobs ?? [];
  const operators = data?.operators ?? [];

  const allJobsMap = useMemo(() => {
    const map = new Map<number, Job>();
    for (const j of liveUnassignedJobs) map.set(j.id, j);
    for (const a of liveAssignments) {
      for (const s of a.stops || []) {
        if (s.job) map.set(s.job.id, s.job);
      }
    }
    return map;
  }, [liveAssignments, liveUnassignedJobs]);

  const { proposedAssignments, proposedUnassignedJobs } = useMemo(() => {
    if (!proposedOptimization) {
      return { proposedAssignments: [], proposedUnassignedJobs: [] };
    }

    const placedJobIds = new Set<number>();

    const pAssignments: RouteAssignment[] = proposedOptimization.solverResult.routes.map((r) => {
      const op = operators.find((o) => o.id === r.operatorId);
      const now = Date.now();
      const stops: RouteStop[] = r.stops.map((s, idx) => {
        placedJobIds.add(s.jobId);
        const job = allJobsMap.get(s.jobId) || {
          id: s.jobId,
          workspaceId: op?.workspaceId ?? 0,
          title: `Job #${s.jobId}`,
          formattedAddress: 'Address on file',
          latitude: null,
          longitude: null,
          serviceDate,
          windowStartOffsetSeconds: null,
          windowEndOffsetSeconds: null,
          timingMode: 'fixed' as const,
          estimatedDurationSeconds: 3600,
          priority: 0,
          status: 'assigned' as const,
          createdAt: now,
          createdBy: 0,
          updatedAt: now,
          updatedBy: 0,
          deletedAt: null,
        };

        return {
          id: -s.jobId,
          workspaceId: op?.workspaceId ?? 0,
          routeAssignmentId: -r.operatorId,
          jobId: s.jobId,
          position: s.position ?? idx,
          plannedArrival: s.plannedArrivalOffsetSeconds,
          plannedDeparture: s.plannedDepartureOffsetSeconds,
          actualArrival: null,
          actualDeparture: null,
          status: 'assigned' as const,
          createdAt: now,
          createdBy: 0,
          updatedAt: now,
          updatedBy: 0,
          job,
        };
      });

      return {
        id: -r.operatorId,
        workspaceId: op?.workspaceId ?? 0,
        operatorProfileId: r.operatorId,
        serviceDate,
        status: 'draft' as const,
        createdAt: now,
        createdBy: 0,
        updatedAt: now,
        updatedBy: 0,
        operator: op,
        stops,
      };
    });

    const pUnassigned: Job[] = Array.from(allJobsMap.values()).filter(
      (j) => !placedJobIds.has(j.id)
    );

    return { proposedAssignments: pAssignments, proposedUnassignedJobs: pUnassigned };
  }, [proposedOptimization, operators, allJobsMap, serviceDate]);

  const isViewingProposal = !!proposedOptimization && planViewMode === 'proposal';
  const assignments = isViewingProposal ? proposedAssignments : liveAssignments;
  const unassignedJobs = isViewingProposal ? proposedUnassignedJobs : liveUnassignedJobs;

  const totalJobsCount =
    unassignedJobs.length + assignments.reduce((sum, a) => sum + (a.stops?.length ?? 0), 0);

  const filteredUnassignedJobs = unassignedJobs.filter((job) => {
    if (!poolSearchQuery.trim()) return true;
    const q = poolSearchQuery.toLowerCase();
    return (
      job.title.toLowerCase().includes(q) ||
      job.formattedAddress.toLowerCase().includes(q) ||
      (job.requiredSkills || []).some((s) => s.name.toLowerCase().includes(q))
    );
  });

  const handleAssign = () => {
    if (!assigningJob || !selectedOperatorId) return;
    assignJobMutation.mutate({
      jobId: assigningJob.id,
      operatorProfileId: Number(selectedOperatorId),
      serviceDate,
    });
  };

  const handleReassign = () => {
    if (!reassigningStop || !targetReassignOpId) return;
    reassignStopMutation.mutate({
      stopId: reassigningStop.stopId,
      targetOperatorProfileId: Number(targetReassignOpId),
    });
  };

  const handleClearRoute = (op: OperatorProfile, stopCount: number) => {
    if (stopCount === 0) return;
    const opName = op.user?.fullName || op.user?.email || `Operator #${op.id}`;
    if (
      confirm(
        `Are you sure you want to clear all ${stopCount} stop(s) for ${opName}? They will be returned to the unassigned pool.`
      )
    ) {
      clearRouteMutation.mutate({
        operatorProfileId: op.id,
        serviceDate,
      });
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-muted/60 p-1 rounded-lg border border-border/40">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => shiftDate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 px-2 font-semibold text-sm">
              <CalendarIcon className="h-4 w-4 text-primary" />
              <span>{serviceDate}</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => shiftDate(1)}>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setServiceDate(today)}
          >
            Today
          </Button>

          {/* Stats Badges */}
          <div className="hidden sm:flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {totalJobsCount} Total Jobs
            </Badge>
            <Badge
              variant="outline"
              className={
                unassignedJobs.length > 0
                  ? 'border-amber-500/40 text-amber-600 bg-amber-500/10'
                  : 'border-border/60 text-muted-foreground'
              }
            >
              {unassignedJobs.length} Unassigned
            </Badge>
          </div>
        </div>

        {/* View Switcher & Action Buttons */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-muted p-1 rounded-lg border border-border/60">
            <Button
              variant={viewMode === 'board' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={() => setViewMode('board')}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              <span>Board</span>
            </Button>
            <Button
              variant={viewMode === 'timeline' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={() => setViewMode('timeline')}
            >
              <Clock className="h-3.5 w-3.5" />
              <span>Timeline</span>
            </Button>
            <Button
              variant={viewMode === 'map' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={() => setViewMode('map')}
            >
              <MapIcon className="h-3.5 w-3.5" />
              <span>Map</span>
            </Button>
          </div>

          <Button
            variant={isAnalyticsOpen ? 'secondary' : 'outline'}
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}
            title="Toggle Route & Fleet Analytics Side Panel"
          >
            <TrendingUp className="h-3.5 w-3.5 text-primary" />
            <span>Analytics</span>
          </Button>

          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={openIngest}>
            <Download className="h-3.5 w-3.5" />
            <span>Ingest</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs text-primary border-primary/30 bg-primary/5 hover:bg-primary/10"
            onClick={() => setIsSlotFinderOpen(true)}
            title="Check capacity and find available slots across your operators"
          >
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span>Find Slot</span>
          </Button>

          <Button
            size="sm"
            className="h-8 gap-1.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => setIsStrategyModalOpen(true)}
            disabled={optimizeDayMutation.isPending || operators.length === 0}
          >
            {optimizeDayMutation.isPending ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                <span>Optimizing...</span>
              </>
            ) : (
              <>
                <Zap className="h-3.5 w-3.5" />
                <span>Optimize Day</span>
              </>
            )}
          </Button>

          <Button
            variant={isCopilotOpen ? 'default' : 'outline'}
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setIsCopilotOpen(!isCopilotOpen)}
            title="Toggle AI Job Plan Copilot"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>AI Copilot</span>
          </Button>
        </div>
      </div>

      {/* Proposal Review Banner */}
      {proposedOptimization && (
        <div className="bg-primary/10 border-b border-primary/20 px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-xs animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary text-primary-foreground shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">
                  Optimization Proposal Review
                </span>
                <Badge variant="default" className="text-[10px] py-0 px-1.5 h-4">
                  {STRATEGY_OPTIONS.find((s) => s.id === proposedOptimization.strategy)?.title ||
                    proposedOptimization.strategy}
                </Badge>
                {proposedOptimization.solverResult.unplacedJobs.length > 0 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] py-0 px-1.5 h-4 border-amber-500/50 text-amber-600 dark:text-amber-400 bg-amber-500/10"
                  >
                    {proposedOptimization.solverResult.unplacedJobs.length} Unplaced
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Inspecting proposed schedule on Board, Timeline, and Map before applying to the live
                board.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Compare Toggle */}
            <div className="flex items-center bg-background/80 p-0.5 rounded-lg border border-border/60 text-xs">
              <button
                type="button"
                onClick={() => setPlanViewMode('live')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  planViewMode === 'live'
                    ? 'bg-muted text-foreground shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Live Schedule
              </button>
              <button
                type="button"
                onClick={() => setPlanViewMode('proposal')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                  planViewMode === 'proposal'
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Sparkles className="h-3 w-3" />
                <span>Proposed Plan</span>
              </button>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => discardRunMutation.mutate({ id: proposedOptimization.runId })}
              disabled={discardRunMutation.isPending || acceptRunMutation.isPending}
            >
              <X className="h-3.5 w-3.5" />
              <span>Discard</span>
            </Button>

            <Button
              size="sm"
              className="h-8 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              onClick={() =>
                acceptRunMutation.mutate({
                  id: proposedOptimization.runId,
                  routes: proposedOptimization.solverResult.routes,
                })
              }
              disabled={
                acceptRunMutation.isPending ||
                discardRunMutation.isPending ||
                proposedOptimization.solverResult.routes.every((r) => r.stops.length === 0)
              }
              title={
                proposedOptimization.solverResult.routes.every((r) => r.stops.length === 0)
                  ? "Nothing to apply -- the optimizer didn't place any jobs. Applying now would only clear the live schedule."
                  : undefined
              }
            >
              {acceptRunMutation.isPending ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  <span>Applying...</span>
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5" />
                  <span>Accept & Apply Plan</span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Total optimization failure -- the solver found no feasible plan at all, distinct from the
          normal per-job "unplaced" case shown in the pool below. Shown separately and persistently
          so a failed run doesn't read as "nothing changed." */}
      {proposedOptimization?.solverResult.routes.every((r) => r.stops.length === 0) && (
        <div className="bg-destructive/10 border-b border-destructive/30 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-destructive">
              Optimizer couldn't place any jobs
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {proposedOptimization.solverResult.status === 'no_solution'
                ? "The solver found no feasible plan for the current operators and jobs within its search time budget. This is usually too many jobs for the active operator(s)' shift hours, or a search timeout -- try adding operators, splitting the job load across days, or check the reasons listed in the Unassigned Pool below."
                : 'Every job was dropped for skill or shift-window reasons -- check the reasons listed in the Unassigned Pool below.'}{' '}
              Nothing on the live schedule has changed; Discard to try again.
            </p>
          </div>
        </div>
      )}

      {/* Main Content Area based on View Mode */}
      <div className="flex-1 flex overflow-hidden bg-muted/20">
        <div className="flex-1 flex overflow-hidden p-4">
          {viewMode === 'map' && (
            <JobPlanMap
              assignments={assignments}
              unassignedJobs={unassignedJobs}
              operators={operators}
              onSelectJob={(job) => {
                setPreviewJob(job);
                setPreviewJobId(job.id);
              }}
            />
          )}

          {viewMode === 'timeline' && (
            <JobPlanTimeline
              assignments={assignments}
              operators={operators}
              onSelectJob={(job) => {
                setPreviewJob(job);
                setPreviewJobId(job.id);
              }}
            />
          )}

          {viewMode === 'board' && (
            <div className="flex-1 flex overflow-x-auto overflow-y-hidden gap-4">
              {/* Unassigned Pool Column */}
              <div className="w-80 flex-shrink-0 flex flex-col bg-card rounded-xl border border-border/60 shadow-sm overflow-hidden">
                <div className="p-3 border-b border-border/40 bg-muted/40 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserX className="h-4 w-4 text-amber-500" />
                      <span className="font-semibold text-sm">Unassigned Pool</span>
                    </div>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {unassignedJobs.length}
                    </Badge>
                  </div>
                  {/* Pool Search Input */}
                  <div className="relative">
                    <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Filter by title, address, skill..."
                      value={poolSearchQuery}
                      onChange={(e) => setPoolSearchQuery(e.target.value)}
                      className="h-7 text-xs pl-7 bg-background"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2.5">
                  {filteredUnassignedJobs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 text-center p-4 text-muted-foreground">
                      <CheckCircle className="h-8 w-8 text-emerald-500 mb-2 opacity-80" />
                      <p className="text-xs font-medium">
                        {unassignedJobs.length === 0
                          ? 'All jobs assigned for this day'
                          : 'No unassigned jobs match filter'}
                      </p>
                    </div>
                  ) : (
                    filteredUnassignedJobs.map((job) => {
                      const unplacedInfo = proposedOptimization?.solverResult.unplacedJobs.find(
                        (u) => u.id === job.id
                      );
                      return (
                        <div key={job.id} className="relative group">
                          <JobCard
                            job={job}
                            onClick={() => {
                              setPreviewJob(job);
                              setPreviewJobId(job.id);
                            }}
                          />
                          {isViewingProposal && unplacedInfo && (
                            <div
                              className="mt-1 flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/20"
                              title={unplacedInfo.detail}
                            >
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              <span className="capitalize font-medium">
                                Unplaced: {unplacedInfo.reason.replace(/_/g, ' ')}
                              </span>
                            </div>
                          )}
                          {!isViewingProposal && (
                            <div className="mt-1 flex justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-[11px] px-2 gap-1 text-primary hover:bg-primary/10"
                                onClick={() => setAssigningJob(job)}
                              >
                                <Plus className="h-3 w-3" />
                                Assign
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Operator Columns */}
              <div className="flex-1 flex gap-4 overflow-x-auto pb-2">
                {operators.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-card rounded-xl border border-dashed border-border/60">
                    <p className="text-sm font-medium text-muted-foreground">
                      No active operators found. Create or activate operators to build routes.
                    </p>
                  </div>
                ) : (
                  operators.map((op) => {
                    const assignment = assignments.find((a) => a.operatorProfileId === op.id);
                    const stops = (assignment?.stops ?? []) as RouteStop[];
                    const sequenceInputs = stops.map((s) => ({
                      id: s.id,
                      jobId: s.jobId,
                      durationSeconds: s.job?.estimatedDurationSeconds ?? 3600,
                      latitude: s.job?.latitude,
                      longitude: s.job?.longitude,
                      windowStartOffsetSeconds: s.job?.windowStartOffsetSeconds,
                      windowEndOffsetSeconds: s.job?.windowEndOffsetSeconds,
                      plannedArrival: s.plannedArrival,
                      plannedDeparture: s.plannedDeparture,
                    }));
                    const feasibility = recomputeStopSequence(sequenceInputs, {
                      shiftStartOffsetSeconds: op.shiftStartOffsetSeconds,
                      shiftEndOffsetSeconds: op.shiftEndOffsetSeconds,
                      breakDurationSeconds: op.breakDurationSeconds,
                      breakWindowStartOffsetSeconds: op.breakWindowStartOffsetSeconds,
                      breakWindowEndOffsetSeconds: op.breakWindowEndOffsetSeconds,
                      startLocation: op.startLocation,
                      endLocation: op.endLocation,
                    });
                    const calculatedStops = feasibility.stops;

                    return (
                      <div
                        key={op.id}
                        className="w-80 flex-shrink-0 flex flex-col bg-card rounded-xl border border-border/60 shadow-sm overflow-hidden"
                      >
                        {/* Operator Header */}
                        <div className="p-3 border-b border-border/40 bg-muted/30 flex flex-col gap-1.5">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm truncate">
                              {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                            </span>
                            <div className="flex items-center gap-1.5">
                              {isViewingProposal && (
                                <Badge
                                  variant="secondary"
                                  className="text-[9px] px-1 py-0 bg-primary/10 text-primary border-primary/30 font-medium"
                                >
                                  PROPOSED
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-[10px]">
                                {stops.length} stop{stops.length !== 1 ? 's' : ''}
                              </Badge>
                              {!isViewingProposal && stops.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 px-1 text-[10px] text-muted-foreground hover:text-destructive"
                                  title="Clear route (return all stops to unassigned pool)"
                                  onClick={() => handleClearRoute(op, stops.length)}
                                  disabled={clearRouteMutation.isPending}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>
                              {formatOffsetTime(op.shiftStartOffsetSeconds)} –{' '}
                              {formatOffsetTime(op.shiftEndOffsetSeconds)}
                            </span>
                          </div>

                          {/* Operator Skills */}
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(op.skills ?? []).map((s) => (
                              <Badge
                                key={s.id}
                                variant="secondary"
                                className="text-[9px] px-1.5 py-0 bg-primary/10 text-primary border-transparent"
                              >
                                {s.name}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Stops List */}
                        <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2.5">
                          {stops.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-36 text-center text-muted-foreground">
                              <p className="text-xs">No jobs assigned</p>
                            </div>
                          ) : (
                            calculatedStops.map((stopTiming: RecomputedStopTiming, idx: number) => {
                              const stop = stops.find((s) => s.jobId === stopTiming.jobId);
                              if (!stop) return null;
                              const job = stop.job;
                              if (!job) return null;

                              const { eligible, missingSkillIds } = checkOperatorSkillEligibility(
                                op,
                                job
                              );

                              return (
                                <div key={stop.id} className="relative flex flex-col gap-1">
                                  {/* Transit indicator before this stop */}
                                  {stopTiming.driveSecondsFromPrevious > 0 && (
                                    <div className="flex items-center gap-1.5 py-0.5 px-2 text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded border border-amber-500/20 w-fit">
                                      <Navigation className="h-2.5 w-2.5 rotate-45 shrink-0" />
                                      <span>
                                        {Math.round(stopTiming.driveSecondsFromPrevious / 60)} min
                                        travel
                                      </span>
                                    </div>
                                  )}

                                  {/* Waiting dead-time indicator */}
                                  {stopTiming.waitingSeconds > 0 && (
                                    <div className="flex items-center gap-1.5 py-0.5 px-2 text-[10px] text-yellow-700 dark:text-yellow-300 bg-yellow-500/15 rounded border border-yellow-500/25 w-fit">
                                      <Clock className="h-2.5 w-2.5 shrink-0" />
                                      <span>
                                        {Math.round(stopTiming.waitingSeconds / 60)} min wait for
                                        window
                                      </span>
                                    </div>
                                  )}

                                  <div className="flex items-center justify-between text-[11px] text-muted-foreground font-medium px-1">
                                    <div className="flex items-center gap-1">
                                      <span>Stop #{idx + 1}</span>
                                      {/* Reorder Arrows */}
                                      {!isViewingProposal && (
                                        <div className="flex items-center">
                                          <button
                                            type="button"
                                            disabled={
                                              idx === 0 || moveStopPositionMutation.isPending
                                            }
                                            onClick={() =>
                                              moveStopPositionMutation.mutate({
                                                stopId: stop.id,
                                                direction: 'up',
                                              })
                                            }
                                            className="p-0.5 hover:text-foreground disabled:opacity-30"
                                            title="Move stop earlier"
                                          >
                                            <ArrowUp className="h-3 w-3" />
                                          </button>
                                          <button
                                            type="button"
                                            disabled={
                                              idx === calculatedStops.length - 1 ||
                                              moveStopPositionMutation.isPending
                                            }
                                            onClick={() =>
                                              moveStopPositionMutation.mutate({
                                                stopId: stop.id,
                                                direction: 'down',
                                              })
                                            }
                                            className="p-0.5 hover:text-foreground disabled:opacity-30"
                                            title="Move stop later"
                                          >
                                            <ArrowDown className="h-3 w-3" />
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {!isViewingProposal && (
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 px-1.5 text-[10px] text-primary hover:bg-primary/10 gap-0.5"
                                          onClick={() =>
                                            setReassigningStop({
                                              stopId: stop.id,
                                              jobTitle: job.title,
                                              currentOpId: op.id,
                                            })
                                          }
                                          title="Reassign to another operator"
                                        >
                                          <ArrowRightLeft className="h-2.5 w-2.5" />
                                          <span>Reassign</span>
                                        </Button>

                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 px-1.5 text-[10px] text-destructive hover:bg-destructive/10"
                                          onClick={() =>
                                            removeStopMutation.mutate({ stopId: stop.id })
                                          }
                                          title="Unassign from route"
                                        >
                                          Unassign
                                        </Button>
                                      </div>
                                    )}
                                  </div>

                                  <JobCard
                                    job={job}
                                    onClick={() => {
                                      setPreviewJob(job);
                                      setPreviewJobId(job.id);
                                    }}
                                    plannedArrivalSeconds={stopTiming?.serviceStartSeconds}
                                    windowViolation={stopTiming?.windowViolation}
                                  />

                                  {!eligible && (
                                    <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 font-medium px-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      <span>
                                        Operator missing {missingSkillIds.length} required skill(s)
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Reusable Route & Fleet Analytics Side Panel */}
        <JobPlanDetailPanel
          assignments={assignments}
          operators={operators}
          unassignedJobs={unassignedJobs}
          serviceDate={serviceDate}
          isViewingProposal={isViewingProposal}
          proposedOptimization={proposedOptimization}
          isOpen={isAnalyticsOpen}
          onToggle={() => setIsAnalyticsOpen(!isAnalyticsOpen)}
          onJobClick={(jobId) => {
            setPreviewJobId(jobId);
            setPreviewJob(null);
          }}
        />

        {/* AI Job Plan Copilot Panel (Far Right) */}
        <JobPlanCopilotPanel
          serviceDate={serviceDate}
          isOpen={isCopilotOpen}
          onToggle={() => setIsCopilotOpen(!isCopilotOpen)}
          onOpenIngestDrawer={openIngest}
        />
      </div>

      {/* Workspace Job Plan Onboarding Modal */}
      <JobPlanOnboardingModal
        isOpen={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        onOpenIngest={openIngest}
      />

      {/* Optimization Strategy Selection Modal */}
      <Dialog
        open={isStrategyModalOpen}
        onOpenChange={(open) => !open && setIsStrategyModalOpen(false)}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              <span>Choose Optimization Goal</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Select how the job plan engine should allocate jobs across your active operators for{' '}
              <span className="font-semibold text-foreground">{serviceDate}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2.5 py-1">
            {STRATEGY_OPTIONS.map((strat) => {
              const Icon = strat.icon;
              const isSelected = selectedStrategy === strat.id;
              return (
                <div
                  key={strat.id}
                  onClick={() => setSelectedStrategy(strat.id)}
                  className={`relative flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    isSelected
                      ? `${strat.colorClass} border-primary shadow-xs`
                      : 'border-border/60 hover:border-border hover:bg-muted/30'
                  }`}
                >
                  <div
                    className={`mt-0.5 p-2 rounded-lg shrink-0 ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm font-semibold text-foreground flex items-center gap-2">
                        {strat.title}
                        {strat.badge && (
                          <Badge
                            variant={isSelected ? 'default' : 'secondary'}
                            className="text-[10px] py-0 px-1.5 h-4"
                          >
                            {strat.badge}
                          </Badge>
                        )}
                      </span>
                      {isSelected ? (
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-border shrink-0" />
                      )}
                    </div>

                    <p className="text-xs text-foreground/90 font-medium">{strat.goal}</p>

                    <div className="mt-1 pt-1.5 border-t border-border/30 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px] text-muted-foreground">
                      <div>
                        <span className="font-medium text-foreground/80">How it behaves: </span>
                        {strat.behavior}
                      </div>
                      <div>
                        <span className="font-medium text-foreground/80">Best for: </span>
                        {strat.bestFor}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-border/40">
            <Button
              variant="outline"
              onClick={() => setIsStrategyModalOpen(false)}
              disabled={optimizeDayMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsStrategyModalOpen(false);
                optimizeDayMutation.mutate({ serviceDate, strategy: selectedStrategy });
              }}
              disabled={optimizeDayMutation.isPending}
              className="gap-1.5"
            >
              {optimizeDayMutation.isPending ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  <span>Optimizing...</span>
                </>
              ) : (
                <>
                  <Zap className="h-3.5 w-3.5" />
                  <span>Run Optimizer</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={!!assigningJob} onOpenChange={(open) => !open && setAssigningJob(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Job to Operator</DialogTitle>
          </DialogHeader>
          {assigningJob && (
            <div className="flex flex-col gap-4 py-2">
              <div>
                <p className="font-semibold text-sm">{assigningJob.title}</p>
                <p className="text-xs text-muted-foreground">{assigningJob.formattedAddress}</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Select Operator</label>
                <Select value={selectedOperatorId} onValueChange={setSelectedOperatorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an operator..." />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map((op) => {
                      const { eligible } = checkOperatorSkillEligibility(op, assigningJob);
                      return (
                        <SelectItem key={op.id} value={String(op.id)}>
                          {op.user?.fullName || op.user?.email}{' '}
                          {eligible ? '✓' : '(Missing skills)'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAssigningJob(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAssign}
                  disabled={!selectedOperatorId || assignJobMutation.isPending}
                >
                  Confirm Assignment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reassign Stop Dialog */}
      <Dialog open={!!reassigningStop} onOpenChange={(open) => !open && setReassigningStop(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Stop to Operator</DialogTitle>
          </DialogHeader>
          {reassigningStop && (
            <div className="flex flex-col gap-4 py-2">
              <div>
                <p className="font-semibold text-sm">{reassigningStop.jobTitle}</p>
                <p className="text-xs text-muted-foreground">
                  Transfer to another active operator route
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Target Operator</label>
                <Select value={targetReassignOpId} onValueChange={setTargetReassignOpId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target operator..." />
                  </SelectTrigger>
                  <SelectContent>
                    {operators
                      .filter((op) => op.id !== reassigningStop.currentOpId)
                      .map((op) => (
                        <SelectItem key={op.id} value={String(op.id)}>
                          {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setReassigningStop(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleReassign}
                  disabled={!targetReassignOpId || reassignStopMutation.isPending}
                >
                  Transfer Stop
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Job Quick Preview Drawer */}
      <JobPreviewSheet
        job={previewJob}
        jobId={previewJobId}
        isOpen={previewJob !== null || previewJobId !== null}
        onClose={() => {
          setPreviewJob(null);
          setPreviewJobId(null);
        }}
        operators={operators}
        onOpenFullJob={(jobId) => navigate(`/org/${orgId}/workspace/${workspaceId}/jobs/${jobId}`)}
        onJobUpdated={() => {
          utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
          utils.jobPlan.jobs.list.invalidate();
        }}
      />

      {/* Standalone Slot Capacity Finder Dialog */}
      <JobSlotFinderDialog
        job={previewJob}
        isOpen={isSlotFinderOpen}
        onClose={() => setIsSlotFinderOpen(false)}
        unassignedJobs={unassignedJobs}
        operators={operators}
        onSlotSelected={(date) => {
          setServiceDate(date);
          utils.jobPlan.getDaySchedule.invalidate({ serviceDate: date });
          utils.jobPlan.jobs.list.invalidate();
        }}
      />
    </div>
  );
}
